import { useMemo } from "react";
import { usePayoutCoverage } from "./useMe";

/**
 * Narrows two country sources down to Blupay's supported payout markets:
 *
 * - `corridorCountries`: from useCorridor().countries (Zuba corridor map —
 *   drives which country codes have channel/field definitions at all)
 * - `geoCountries`: from useCountries() (name/dial-code metadata used to
 *   render the picker)
 *
 * A country only ends up in the dropdown if it's present in BOTH sources
 * AND returned by GET /me/corridors (usePayoutCoverage) — so a
 * Zuba-supported country no route can reach from the customer's country
 * (or a geo country Zuba doesn't know) is excluded either way. While the
 * corridors load, no country is offered.
 */
export function useSupportedRecipientCountries(
  corridorCountries,
  geoCountries
) {
  const { supportedCountryCodes } = usePayoutCoverage();

  const supportedCorridorCountries = useMemo(
    () =>
      (corridorCountries ?? []).filter((c) =>
        supportedCountryCodes.includes(
          c.code?.toUpperCase()
        )
      ),
    [corridorCountries, supportedCountryCodes]
  );

  const supportedCodeSet = useMemo(
    () =>
      new Set(
        supportedCorridorCountries.map((c) =>
          c.code.toUpperCase()
        )
      ),
    [supportedCorridorCountries]
  );

  const countryOptions = useMemo(
    () =>
      (geoCountries ?? [])
        .filter((c) =>
          supportedCodeSet.has(
            c.code?.toUpperCase()
          )
        )
        .map((c) => ({
          value: c.code.toUpperCase(),
          label: c.name,
        })),
    [geoCountries, supportedCodeSet]
  );

  return {
    supportedCorridorCountries,
    countryOptions,
  };
}